<script setup>
import { useCampaignRules } from "@/composables/campaign-rules";
import { notificationsMixin } from "@/mixins";
import { nanoid } from "nanoid";
import { getCurrentInstance, reactive, ref } from "vue";
import CampaignRulesEmailTemplate from "./campaignRulesEmailTemplate.vue";
import CampaignRulesSmsTemplate from "./campaignRulesSmsTemplate.vue";

const emit = defineEmits(["submit"]);

const {
  loading: loadingCampaignRules,
  createOrgCampaignRules,
  updateOrgCampaignRules,
} = useCampaignRules();

const vm = getCurrentInstance();
const $t = vm.proxy.$t.bind(vm.proxy);
const cId = vm.proxy.$store.state.campaign.campaign.data.id;
const notificationsMixinHandleCatch =
  notificationsMixin.methods.$_notificationsMixin_handleCatch.bind(vm.proxy);
const notificationsMixinMakeToastCompat =
  notificationsMixin.methods.$_notificationsMixin_makeToastCompat.bind(vm.proxy);

const MODAL_ID = `campaign-rule-modal-${cId}-${nanoid()}`;
const FORM_ID = `campaign-rule-modal-form-${cId}-${nanoid()}`;

const ruleActionList = ref(["email", "sms"]);
const ruleTypeList = ref(["goal_progress"]);
const modalTypeCreate = ref(false);

const formData = reactive({
  action: "email",
  active: true,
  meta: {
    percentage: 0,
  },
  rule_type: "goal_progress",
  template_id: null,
});

async function show(item) {
  if (!item) {
    modalTypeCreate.value = true;
    vm.proxy.$bvModal.show(MODAL_ID);

    return;
  }

  formData.meta = Object.assign({ percentage: 0 }, item.meta || {});
  formData.action = item.action;
  formData.active = item.active;
  formData.rule_type = item.rule_type;
  formData.template_id = item.template_id;
  formData.id = item.id;

  vm.proxy.$bvModal.show(MODAL_ID);
}

async function hide() {
  vm.proxy.$bvModal.hide(MODAL_ID);
}

function clearData() {
  modalTypeCreate.value = false;
  formData.action = "email";
  formData.active = true;
  formData.meta = {
    percentage: 0,
  };
  formData.rule_type = "goal_progress";
  formData.template_id = null;
  delete formData.id;
}

async function submit() {
  const formDataClone = structuredClone(formData);
  formDataClone.meta.percentage = Number(formDataClone.meta.percentage || 0);

  try {
    if (modalTypeCreate.value) {
      await createOrgCampaignRules(cId, formDataClone);
    } else {
      await updateOrgCampaignRules(cId, formDataClone.id, formDataClone);
    }

    notificationsMixinMakeToastCompat({
      group: "Notification",
      title: $t("edit_campaign.success_save_campaign_rule", "Saved"),
    });
    emit("submit", hide);
  } catch (e) {
    notificationsMixinHandleCatch(e);
  }
}
</script>

<template>
  <div>
    <slot :show="show" :loading="loadingCampaignRules" />
    <b-modal :id="MODAL_ID" @hide="clearData" size="xl" scrollable centered>
      <template #modal-header="{ hide }">
        <div>
          <h2>{{ $t("edit_campaign.campaign_rules_modal_title", "Rules") }}</h2>
          <div class="modal-header__hide" @click="hide">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
              />
              <path d="M0 0h24v24H0z" fill="none" />
            </svg>
          </div>
        </div>
      </template>
      <template #modal-footer="{ hide }">
        <div>
          <button type="button" class="bttn bttn--lg bttn--blue" @click="hide" name="button">
            {{ $t("edit_campaign.campaign_rules_modal_close", "Close") }}
          </button>
          <button
            type="submit"
            :form="FORM_ID"
            :disabled="loadingCampaignRules"
            :class="{
              disabled: loadingCampaignRules,
            }"
            class="bttn bttn--lg bttn--orange"
          >
            {{ $t("edit_campaign.campaign_rules_modal_save", "Save") }}
          </button>
        </div>
      </template>
      <form :id="FORM_ID" @submit.prevent="submit">
        <b-form-group :label="$t('edit_campaign.campaign_rule_select_action', 'Select Action')">
          <b-form-select
            v-model="formData.action"
            :options="ruleActionList"
            @change="formData.template_id = null"
            required
          >
            <template v-slot:first>
              <b-form-select-option :value="null" disabled>
                {{ $t("edit_campaign.campaign_rule_select_action", "Select Action") }}
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-form-group
          :label="$t('edit_campaign.campaign_rule_select_rule_type', 'Select Rule Type')"
        >
          <b-form-select v-model="formData.rule_type" :options="ruleTypeList" required>
            <template v-slot:first>
              <b-form-select-option :value="null" disabled>
                {{ $t("edit_campaign.campaign_rule_select_rule_type", "Select Rule Type") }}
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-form-group
          v-if="formData.rule_type === 'goal_progress'"
          :label="$t('edit_campaign.campaign_rule_meta_persentage', 'Goal Percentage')"
        >
          <b-form-input
            required
            type="number"
            no-wheel
            min="0"
            max="100"
            v-model="formData.meta.percentage"
          />
        </b-form-group>
      </form>
      <CampaignRulesEmailTemplate
        v-if="formData.action === 'email'"
        @templeteId="formData.template_id = $event"
        :templateId="formData.template_id"
      />
      <CampaignRulesSmsTemplate
        v-if="formData.action === 'sms'"
        @templeteId="formData.template_id = $event"
        :templateId="formData.template_id"
      />
    </b-modal>
  </div>
</template>
